import { FullPageCard, NavigationPage, AlertMessage } from '@/app/components'
import Button from '@/components/Button/Button'
import Checkbox from '@/components/forms/checkbox'
import { Sun, Wind } from 'lucide-react'
import useViewModel from './useInactiveTimeSchedulesViewModel'
import { formatInlineList } from '@/app/pages/thermostat/cost-categories/inactive-times/utils'
import Modal from '@/components/Modal'
import { useHistory } from 'react-router-dom'

export default function ThermostatCostCategoriesInactiveTimeSchedules() {
  const viewModel = useViewModel()
  const history = useHistory()

  return (
    <FullPageCard>
      <NavigationPage
        id="cost-categories-inactive-time-schedules"
        title="Set Inactive Times"
        subtitle="Set time(s) when you want Save Money to be inactive based on your schedule."
        className="p-0"
        navigationOptions={['back']}
        backOverride={() =>
          viewModel.onNavigationAttempt(() => history.goBack())
        }
      >
        <div className="flex flex-col h-full gap-4">
          <AlertMessage variant="info">
            {(() => {
              const selectedNames = viewModel.comfortSettings
                .filter((item) =>
                  viewModel.inactiveComfortSettingIds.has(item.id),
                )
                .map((item) => item.name)
                .filter(Boolean)

              const summary = formatInlineList(selectedNames)
              return summary ? (
                <div>
                  <div className="text-sm font-medium text-base-grey-700">
                    Save Money will be inactive for:
                  </div>
                  <div className="text-sm font-bold text-base-grey-600">
                    {summary}
                  </div>
                </div>
              ) : (
                <div>Save Money is currently running at all times</div>
              )
            })()}
          </AlertMessage>

          <div className="rounded-xl border border-solid border-base-grey-200 bg-white">
            <div className="px-4 pt-4 text-sm font-bold text-base-grey-700">
              Select inactive times based on your schedule:
            </div>

            <div className="p-4 pt-2 flex flex-col gap-3">
              {viewModel.comfortSettings.map((item) => {
                const checked = viewModel.inactiveComfortSettingIds.has(item.id)
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between rounded-xl border border-base-grey-200 bg-base-grey-100 px-4 py-3 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => viewModel.onToggle(item.id, !checked)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        viewModel.onToggle(item.id, !checked)
                      }
                    }}
                  >
                    <div className="flex flex-col">
                      <div className="text-sm font-bold text-base-grey-700">
                        {item.name}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-xs text-base-grey-500">
                        <div className="flex items-center gap-1">
                          <Wind className="text-themed-blue-500" size={12} />
                          <span>{item.cool_setpoint}°F</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Sun className="text-themed-red-500" size={12} />
                          <span>{item.heat_setpoint}°F</span>
                        </div>
                      </div>
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        onChange={(v) => viewModel.onToggle(item.id, v)}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex-1" />

          <Button
            id="save-inactive-time-schedules"
            variant="primary"
            className="mt-6"
            disabled={!viewModel.hasChanges}
            onClick={viewModel.onSave}
          >
            Save
          </Button>
        </div>
      </NavigationPage>

      {/* Navigation Confirmation Modal */}
      <Modal
        id="confirm-navigation"
        open={viewModel.showConfirmationModal}
        onClose={viewModel.onCancelNavigation}
        title="Do you want to save your edits?"
        maxWidth="440px"
      >
        <AlertMessage variant="info">
          {(() => {
            const selectedNames = viewModel.comfortSettings
              .filter((item) =>
                viewModel.inactiveComfortSettingIds.has(item.id),
              )
              .map((item) => item.name)
              .filter(Boolean)

            const summary = formatInlineList(selectedNames)
            return summary ? (
              <div>
                <div className="text-sm font-medium text-base-grey-700">
                  Save Money will be inactive for:
                </div>
                <div className="text-sm font-bold text-base-grey-600">
                  {summary}
                </div>
              </div>
            ) : (
              <div>Save Money is currently running at all times</div>
            )
          })()}
        </AlertMessage>

        <Modal.Actions>
          <Button
            id="discard-navigation"
            variant="secondary"
            onClick={viewModel.onDiscardAndNavigate}
          >
            Discard
          </Button>
          <Button
            id="confirm-navigation"
            variant="primary"
            onClick={viewModel.onSaveAndNavigate}
          >
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    </FullPageCard>
  )
}
